import { useState, lazy, Suspense, useRef, useEffect, useCallback } from "react";
import {
  SkyAvatar,
  SkyButton,
  SkyText,
  SkyLink,
  SkySkeleton,
  SkySkeletonItem,
  SkyLoader,
} from "@skypointcloud/skypoint-ui";
import { Clock16Regular, Open16Regular, SignOut20Regular } from "@fluentui/react-icons";
import { useCurrentUser } from "../hooks";
import { TenantInstanceSwitcher } from "./TenantInstanceSwitcher";
import { useDisplayTz, useStoredDisplayTz } from "../../../../hooks/useDisplayTz";
import { displayTimezoneLabel } from "../../../../common/constants/displayTimezones";
import { formatInstant } from "../../../../common/utilities/displayTime";
import { ThemeSwitcher } from "../../../../components/theme/ThemeSwitcher";

// Lazy-load dialogs - only loaded when user opens them
const FeedbackDialog = lazy(() =>
  import("./FeedbackDialog").then((m) => ({ default: m.FeedbackDialog })),
);
const TimezoneDialog = lazy(() =>
  import("./TimezoneDialog").then((m) => ({ default: m.TimezoneDialog })),
);

interface ProfileMenuProps {
  onViewAccount: () => void;
  onSignOut: () => void;
}

export function ProfileMenu({ onViewAccount, onSignOut }: ProfileMenuProps) {
  // Get user data from hook
  const { currentUser, initials, isLoaded: isUserLoaded } = useCurrentUser();

  // The viewer's display zone, for the timestamps this menu renders itself.
  const zone = useDisplayTz();
  // What they have actually stored, for the Time zone row's label. A new user
  // has nothing stored yet, and the row should say so rather than read "UTC".
  const storedZone = useStoredDisplayTz();

  // Dialog states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showTzDialog, setShowTzDialog] = useState(false);

  // Context change loading state (for tenant/instance switch)
  const [isChangingContext, setIsChangingContext] = useState(false);

  // Menu state - click-outside handled via ref + effect
  const [menuOpen, setMenuOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
      setMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen, handleClickOutside]);

  const name = currentUser?.fullname || "";
  const email = currentUser?.email || "";
  // Rendered in the viewer's chosen zone rather than the browser's, so this menu
  // does not contradict the setting it is offering two rows below.
  const lastLogin = currentUser?.lastLogin ? formatInstant(currentUser.lastLogin, zone) : null;

  return (
    <>
      <div className="main-header relative">
        {/* Trigger */}
        {!isUserLoaded ? (
          <SkySkeleton
            aria-label="Loading user"
            className="flex items-center gap-2"
          >
            <SkySkeletonItem
              shape="circle"
              className="w-8! h-8!"
            />
            <SkySkeletonItem className="w-24! h-4! hidden! md:block!" />
          </SkySkeleton>
        ) : (
          <div
            ref={popoverRef}
            className="relative"
          >
            <div
              className="flex items-center gap-2 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setMenuOpen((prev) => !prev)}
              onKeyDown={(e) => e.key === "Enter" && setMenuOpen((prev) => !prev)}
            >
              <SkyAvatar
                name={initials}
                size={32}
                className="hover:ring-2 hover:ring-header-foreground/30 transition-all"
                color="brand"
              />
              {name && (
                <SkyText
                  size={400}
                  weight="medium"
                  className="text-header-foreground hidden! md:block!"
                >
                  {name}
                </SkyText>
              )}
            </div>
            {menuOpen && (
              // #1468: held to the viewport under the 56px header (plus the 8px gap and 8px
              // clear at the bottom) and scrolling inside itself, so Logout stays reachable on a
              // short screen. Where it fits, nothing changes.
              <div
                data-testid="profile-menu-panel"
                className="absolute right-0 top-full p-0 mt-2 rounded-lg shadow-lg border border-gray-200 dark:border-border min-w-75 max-h-[calc(100dvh-4.5rem)] overflow-y-auto overscroll-contain bg-white dark:bg-popover text-gray-800 dark:text-popover-foreground z-50"
              >
                {/* Profile Header */}
                <div className="flex items-start gap-3 p-4 border-b border-gray-200 dark:border-border">
                  <SkyAvatar
                    name={initials}
                    size={40}
                    color="brand"
                  />
                  <div className="flex flex-col min-w-0 flex-1">
                    <SkyText
                      weight="semibold"
                      className="text-gray-900 dark:text-foreground truncate"
                      size={400}
                    >
                      {name}
                    </SkyText>
                    {email && (
                      <SkyText
                        size={300}
                        className="text-gray-500 dark:text-muted-foreground truncate"
                      >
                        {email}
                      </SkyText>
                    )}
                    <SkyLink
                      onClick={() => {
                        onViewAccount();
                        setMenuOpen(false);
                      }}
                      className="text-sm mt-1"
                    >
                      View profile
                    </SkyLink>
                    {lastLogin && (
                      <SkyText
                        size={200}
                        className="text-gray-400 dark:text-muted-foreground mt-1"
                      >
                        Last login: {lastLogin}
                      </SkyText>
                    )}
                  </div>
                </div>

                {/* Tenant/Instance Switcher */}
                <TenantInstanceSwitcher
                  onContextChange={() => setMenuOpen(false)}
                  onLoadingChange={setIsChangingContext}
                />

                {/* Preferences. Sits with the tenant and instance switchers because it
                    answers the same question they do: which context am I looking at
                    this data in. Labelled and divided so it reads as a setting rather
                    than as another link in the list below. */}
                <div className="px-4 pt-3 pb-1">
                  <SkyText
                    size={100}
                    className="text-gray-400 dark:text-muted-foreground uppercase tracking-wide font-semibold"
                  >
                    Preferences
                  </SkyText>
                </div>
                <div className="flex flex-col">
                  <SkyButton
                    appearance="transparent"
                    className="justify-between! px-4! text-gray-800! dark:text-foreground! hover:bg-gray-50! dark:hover:bg-white/5!"
                    onClick={() => {
                      setShowTzDialog(true);
                      setMenuOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <Clock16Regular className="text-gray-400 dark:text-muted-foreground" />
                      Time zone
                    </span>
                    <span className="text-gray-500 dark:text-muted-foreground text-xs">
                      {displayTimezoneLabel(storedZone)}
                    </span>
                  </SkyButton>
                  {/* Appearance. Applies at once; the menu stays open so the change is
                      visible before the user moves on. */}
                  <div className="flex flex-col gap-2 px-4 py-2">
                    <SkyText
                      size={300}
                      className="text-gray-800 dark:text-foreground"
                    >
                      Appearance
                    </SkyText>
                    <ThemeSwitcher className="w-full" />
                  </div>
                </div>

                <div className="mx-4 my-1 border-t border-gray-100 dark:border-border" />

                {/* Menu Items */}
                <div className="py-2 flex flex-col">
                  <SkyButton
                    appearance="transparent"
                    className="justify-between! px-4! text-gray-800! dark:text-foreground! hover:bg-gray-50! dark:hover:bg-white/5!"
                    onClick={() => {
                      window.open("https://docs.skypointcloud.com/docs/skyhire", "_blank");
                      setMenuOpen(false);
                    }}
                  >
                    Documentation
                    <Open16Regular className="text-gray-400 dark:text-muted-foreground" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    className="justify-between! px-4! text-gray-800! dark:text-foreground! hover:bg-gray-50! dark:hover:bg-white/5!"
                    onClick={() => {
                      window.open("https://privacy.skypoint.ai/", "_blank");
                      setMenuOpen(false);
                    }}
                  >
                    Privacy Portal
                    <Open16Regular className="text-gray-400 dark:text-muted-foreground" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    className="justify-between! px-4! text-gray-800! dark:text-foreground! hover:bg-gray-50! dark:hover:bg-white/5!"
                    onClick={() => {
                      window.open(
                        "https://skypoint.ai/master-services-agreement-8-7-2025/",
                        "_blank",
                      );
                      setMenuOpen(false);
                    }}
                  >
                    Terms of Use
                    <Open16Regular className="text-gray-400 dark:text-muted-foreground" />
                  </SkyButton>

                  <SkyButton
                    appearance="transparent"
                    className="justify-start! px-4! text-gray-800! dark:text-foreground! hover:bg-gray-50! dark:hover:bg-white/5!"
                    onClick={() => {
                      setShowFeedbackModal(true);
                      setMenuOpen(false);
                    }}
                  >
                    Feedback
                  </SkyButton>
                </div>

                {/* Logout & Version */}
                <div className="p-4 pt-2 flex flex-col gap-2 border-t border-gray-200 dark:border-border">
                  <SkyButton
                    appearance="primary"
                    icon={SignOut20Regular}
                    onClick={() => {
                      onSignOut();
                      setMenuOpen(false);
                    }}
                    className="w-full"
                  >
                    Logout
                  </SkyButton>
                  <SkyText
                    size={200}
                    className="text-gray-400 dark:text-muted-foreground text-center"
                  >
                    Version {__APP_VERSION__}
                  </SkyText>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Feedback Dialog - lazy loaded */}
      {showFeedbackModal && (
        <Suspense fallback={null}>
          <FeedbackDialog
            open={showFeedbackModal}
            onOpenChange={setShowFeedbackModal}
          />
        </Suspense>
      )}

      {/* Time zone Dialog - lazy loaded */}
      {showTzDialog && (
        <Suspense fallback={null}>
          <TimezoneDialog
            open={showTzDialog}
            onClose={() => setShowTzDialog(false)}
          />
        </Suspense>
      )}

      {/* Blocking overlay during tenant/instance context change */}
      {isChangingContext && (
        <div className="fixed inset-0 z-99999 bg-white dark:bg-background flex items-center justify-center">
          <SkyLoader
            size="large"
            label="Loading..."
          />
        </div>
      )}
    </>
  );
}
